/**
 * RoundTimeline - Horizontal timeline of image generation rounds.
 *
 * Shows round indicators with thumbnails. Clicking a past round
 * triggers a confirmation dialog before navigating back (which
 * truncates forward history).
 *
 * Design: DENiZEN with mid-green/dark-green palette.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import type { RoundState } from '@/hooks/useImageGeneration'

export interface RoundTimelineProps {
  rounds: RoundState[]
  currentRoundIndex: number
  onNavigateToRound: (roundIndex: number) => void
  disabled?: boolean
}

interface ConfirmState {
  roundIndex: number
  roundNumber: number
}

export function RoundTimeline({
  rounds, currentRoundIndex, onNavigateToRound, disabled = false,
}: RoundTimelineProps) {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Capture navigation target at dialog-open time so rounds mutations
  // between open and confirm don't cause stale-index bugs
  const pendingNavRef = useRef<ConfirmState | null>(null)

  // Auto-scroll to show the latest round
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
  }, [currentRoundIndex])

  const handleClick = useCallback((idx: number) => {
    if (disabled || idx >= currentRoundIndex) return
    const target: ConfirmState = { roundIndex: idx, roundNumber: rounds[idx]?.roundNumber ?? idx + 1 }
    pendingNavRef.current = target
    setConfirmState(target)
  }, [disabled, currentRoundIndex, rounds])

  const handleConfirm = useCallback(() => {
    const target = pendingNavRef.current
    if (target) {
      onNavigateToRound(target.roundIndex)
      pendingNavRef.current = null
      setConfirmState(null)
    }
  }, [onNavigateToRound])

  const handleCancel = useCallback(() => {
    pendingNavRef.current = null
    setConfirmState(null)
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent, idx: number) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(idx) }
  }, [handleClick])

  const getThumbnail = (round: RoundState): string | null => {
    if (round.selectedVariantIndex !== null) {
      const selected = round.variations[round.selectedVariantIndex]
      if (selected) return selected.image_url
    }
    return round.variations[0]?.image_url ?? null
  }

  // Nothing to render when there are no rounds
  if (rounds.length === 0) return null

  const roundsToDiscard = confirmState ? currentRoundIndex - confirmState.roundIndex : 0

  return (
    <div className="w-full mb-6">
      <p className="text-xs font-body font-bold text-muted tracking-wider mb-2">
        Round History
      </p>

      <div ref={scrollRef} className="flex items-center gap-2 overflow-x-auto pb-2"
        role="list" aria-label="Image generation round history">
        {rounds.map((round, idx) => {
          const isCurrent = idx === currentRoundIndex
          const isPast = idx < currentRoundIndex
          const thumb = getThumbnail(round)
          const hasSelection = round.selectedVariantIndex !== null

          return (
            <div key={`round-${idx}`} className="flex items-center" role="listitem">
              {idx > 0 && <div className="w-4 h-0.5 bg-dark-green/20 flex-shrink-0 -mx-0.5" aria-hidden="true" />}
              <button type="button" role="button"
                aria-label={isCurrent ? `Round ${round.roundNumber} (current)` : `Go back to round ${round.roundNumber}`}
                aria-current={isCurrent ? 'step' : undefined}
                tabIndex={isPast && !disabled ? 0 : -1}
                onClick={() => handleClick(idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                disabled={disabled || isCurrent}
                className={`relative flex-shrink-0 w-14 h-14 border border-dark-green/15 overflow-hidden rounded-2xl transition-all duration-150
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green focus-visible:ring-offset-1
                  ${isCurrent ? 'ring-2 ring-mid-green ring-offset-1' : ''}
                  ${isPast && !disabled ? 'cursor-pointer hover:-translate-y-0.5' : 'cursor-default'}
                  ${disabled ? 'opacity-50' : ''}`}
                style={{ boxShadow: isCurrent ? 'var(--shadow-md)' : 'var(--shadow-denizen-sm)' }}
              >
                {thumb ? (
                  <img src={thumb} alt={`Round ${round.roundNumber} ${hasSelection ? 'selected' : 'preview'}`}
                    className="w-full h-full object-cover" loading="lazy"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                ) : (
                  <div className="w-full h-full bg-stone flex items-center justify-center">
                    <span className="text-xs font-body text-muted">{round.variations.length}</span>
                  </div>
                )}
                <div className={`absolute top-0 left-0 w-5 h-5 flex items-center justify-center
                  text-[10px] font-display font-bold border-r border-b border-dark-green/15
                  ${isCurrent ? 'bg-light-green text-dark-green' : 'bg-white text-dark-green'}`}>
                  {round.roundNumber}
                </div>
                {hasSelection && !isCurrent && (
                  <div className="absolute bottom-0 right-0 w-4 h-4 bg-light-green border-l border-t border-dark-green/15 rounded-tl-lg flex items-center justify-center" aria-hidden="true">
                    <svg className="w-2.5 h-2.5 text-dark-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            </div>
          )
        })}
      </div>

      {confirmState && rounds.length > 0 && (
        <div className="mt-3 p-3 border border-dark-green/15 bg-white font-body text-sm rounded-2xl shadow-denizen-sm"
          role="alertdialog" aria-label={`Confirm navigation to round ${confirmState.roundNumber}`}>
          <p className="text-dark-green mb-3">
            Go back to <strong>Round {confirmState.roundNumber}</strong>?
            {roundsToDiscard === 1
              ? ' You\u2019ll lose 1 round after this.'
              : ` You\u2019ll lose ${roundsToDiscard} rounds after this.`}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={handleConfirm}
              className="px-3 py-1.5 border border-dark-green/15 bg-dark-green font-display font-bold text-xs rounded-2xl
                cursor-pointer hover:-translate-y-0.5 transition-transform shadow-denizen-sm
                focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green text-white">
              Go Back
            </button>
            <button type="button" onClick={handleCancel}
              className="px-3 py-1.5 border border-dark-green/15 bg-white font-display font-bold text-xs rounded-2xl
                cursor-pointer hover:-translate-y-0.5 transition-transform shadow-denizen-sm
                focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green text-dark-green">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default RoundTimeline
